import React from 'react';
import { Button } from '@/components/ui/button';
import { Shield, Star, ArrowRight, CheckCircle } from 'lucide-react';

const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-primary">
      {/* Background Pattern */}
      <div className="absolute inset-0 hero-pattern" />
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-light/20 to-primary opacity-90" />
      
      {/* Content */}
      <div className="section-container relative z-10 py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Column - Text Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/10 border border-gold/30 mb-8 animate-fade-in">
              <Shield className="w-4 h-4 text-gold" />
              <span className="text-sm font-medium text-gold">
                SRA Regulated Law Firm
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground leading-tight mb-6 animate-fade-in-up">
              Reclaim Hidden Commission on Your{' '}
              <span className="gradient-text-gold">Car Finance</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg sm:text-xl text-primary-foreground/80 leading-relaxed mb-4 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-100">
              You could be owed an average of{' '}
              <span className="font-bold text-gold">£830*</span> per agreement 
              for mis-sold car finance.
            </p>
            <p className="text-base text-primary-foreground/70 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-100">
              Our online tool can help you find ALL of your car finance agreements 
              between April 2007 – October 2024.{' '}
              <span className="font-semibold text-primary-foreground">No win, no fee.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-4 animate-fade-in-up animation-delay-200">
              <Button variant="gold" size="xl" className="animate-pulse-glow" asChild>
                <a href="https://signup.bluelionclaims.co.uk/" target="_blank" rel="noopener noreferrer">
                  Find Your Agreements
                  <ArrowRight className="w-5 h-5" />
                </a>
              </Button>
              <Button variant="ghost-light" size="xl" className="border border-primary-foreground/20">
                Learn More
              </Button>
            </div>

            {/* GAP Insurance Supporting Line */}
            <p className="text-sm text-primary-foreground/60 mb-10 max-w-xl mx-auto lg:mx-0 animate-fade-in-up animation-delay-200">
              Also dealing with mis-sold GAP insurance sold alongside car finance agreements.{' '}
              <a href="/gap-insurance-claims" className="text-gold hover:underline">
                Learn more →
              </a>
            </p>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-300">
              {/* SRA Badge */}
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10">
                <Shield className="w-5 h-5 text-accent" />
                <span className="text-sm font-medium text-primary-foreground/90">
                  SRA Regulated
                </span>
              </div>

              {/* No Win No Fee Badge */}
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-foreground/5 border border-primary-foreground/10">
                <CheckCircle className="w-5 h-5 text-success" />
                <span className="text-sm font-medium text-primary-foreground/90">
                  No Win, No Fee
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Hero Video */}
          <div className="relative hidden lg:block animate-fade-in animation-delay-200">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              {/* Main Video */}
              <video
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-[420px] object-cover"
              >
                <source 
                  src="/bluelion-hero-small.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Stats Card - Below Video */}
            <div className="mt-4 p-5 rounded-xl glass-card backdrop-blur-md bg-background/90 border border-border/50 shadow-xl">
              <div className="flex items-center justify-between">
                <div className="text-center flex-1">
                  <p className="text-sm text-muted-foreground mb-1">FCA estimated average</p>
                  <p className="text-2xl font-bold text-foreground">£830*</p>
                </div>
                <div className="w-px h-12 bg-border" />
                <div className="text-center flex-1">
                  <p className="text-sm text-muted-foreground mb-1">Estimated redress</p>
                  <p className="text-2xl font-bold text-gold">£7.5bn</p>
                </div>
                <div className="w-px h-12 bg-border" />
                <div className="text-center flex-1">
                  <p className="text-sm text-muted-foreground mb-1">SRA Regulated</p>
                  <p className="text-2xl font-bold text-foreground">Law Firm</p>
                </div>
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-gold/20 blur-2xl" />
            <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-accent/20 blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export { HeroSection };
